import { AfterViewInit, Directive, HostListener, inject, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';

@Directive({
  selector: '[appLeadingZero]',
  standalone: false
})
export class LeadingZeroDirective implements AfterViewInit, OnDestroy {
  private inputNumber = inject(InputNumber, { self: true });
  private ngControl = inject(NgControl);

  private originalMinFractionDigits: number | undefined;
  private isOverridden = false;

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.inputNumber?.input?.nativeElement) {
        this.inputNumber.input.nativeElement.addEventListener('blur', this.onBlur);
      } else {
        console.error(
          'LeadingZeroDirective: Konnte den `blur`-Listener nicht anhängen, da das interne <input>-Element von p-input-number nicht gefunden wurde.'
        );
      }
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.inputNumber?.input?.nativeElement) {
      this.inputNumber.input.nativeElement.removeEventListener('blur', this.onBlur);
    }
    this.restoreMinFractionDigits();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== ',' && event.key !== '.') {
      return;
    }

    const inputElement = event.target as HTMLInputElement;

    const isValueEmpty = !inputElement.value || inputElement.value.trim() === '';
    const isAllTextSelected =
      inputElement.selectionStart === 0 && inputElement.selectionEnd === inputElement.value.length && inputElement.value.length > 0;

    if (isValueEmpty || isAllTextSelected) {
      //Standard-Aktion von p-input-number verhindern.
      event.preventDefault();

      //minFractionDigits temporär deaktivieren.
      this.overrideMinFractionDigits();

      const newValue = '0,';
      inputElement.value = newValue;
      //Die Komponente asynchron informieren UND DANACH den Cursor setzen.
      setTimeout(() => {
        //Cursor verschieben
        inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        inputElement.value = newValue;
        inputElement.setSelectionRange(newValue.length, newValue.length);
      }, 0);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text');

    if (pastedText && (pastedText.startsWith(',') || pastedText.startsWith('.'))) {
      event.preventDefault();

      const inputElement = event.target as HTMLInputElement;
      const newValue = '0' + pastedText.replace('.', ',');

      this.applyValueAndNotify(inputElement, newValue);
    }
  }

  // Wird als Arrow-Function definiert, um den 'this'-Kontext für den Event-Listener zu erhalten.
  private onBlur = (): void => {
    this.restoreMinFractionDigits();
  };

  /**
   * Zentralisierte Methode, um den Wert zu setzen und p-input-number zu informieren.
   * Gewinnt das "Wettrennen" gegen die interne Logik der Komponente.
   */
  private applyValueAndNotify(inputElement: HTMLInputElement, viewValue: string): void {
    this.overrideMinFractionDigits();

    setTimeout(() => {
      const numericString = viewValue.replace(',', '.');
      const numericValue = parseFloat(numericString);

      if (this.ngControl.control && !isNaN(numericValue)) {
        this.ngControl.control.setValue(numericValue, { emitEvent: true });
        inputElement.value = viewValue;
        inputElement.setSelectionRange(viewValue.length, viewValue.length);
      }
    }, 0);
  }

  /**
   * Speichert die ursprüngliche minFractionDigits und deaktiviert sie auf der Komponente.
   */
  private overrideMinFractionDigits(): void {
    if (this.inputNumber && !this.isOverridden) {
      this.originalMinFractionDigits = this.inputNumber.minFractionDigits;
      this.isOverridden = true;
      // Setzen auf undefined deaktiviert die Logik in p-input-number
      this.inputNumber.minFractionDigits = undefined;
    }
  }

  /**
   * Stellt die ursprüngliche minFractionDigits wieder her und stößt eine Aktualisierung an.
   */
  private restoreMinFractionDigits(): void {
    if (this.inputNumber && this.isOverridden) {
      this.inputNumber.minFractionDigits = this.originalMinFractionDigits;
      this.isOverridden = false;

      setTimeout(() => {
        const control = this.ngControl.control;
        if (control && this.inputNumber) {
          this.inputNumber.writeValue(control.value);
        }
      }, 0);
    }
  }
}
